import { Box, ButtonSize, Icon, IconSize, Select, Text, TextFieldSize, useModalState } from '@bechtel/gds';
import { Question, UiMessageKey, UiTextEnum, UserAccountLayout } from '@bechtel/sr-types';
import { FC, lazy, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import {
  ChangeManagementPermissions,
  ChangeManagementSignature,
  ChangeManagementToolBar,
  ChangeManagementToolbarProvider,
  CloneAction,
  CreateAction,
  DeleteAction,
  IssueCommodity,
  MultiLingualText,
  QuestionModalResponse,
  SearchField,
  useLanguage,
} from '~/components';
import CommodityMenu from '~/components/organisms/commodity-header/menu';
import { useDeleteUserLayout } from '../../hooks';
import { ButtonStyled } from '../../styled';
import { DefaultLayoutProps, defaultLayout } from '../../types';
const MessageModal = lazy(() => import('~/components/organisms/message-modal/message'));

interface ClientGridHeaderProps {
  onSearchTextChanged: (val: string) => void;
  layoutVisibleFlag: boolean;
  setLayoutVisibleFlag: (val: boolean) => void;
  saveLayoutModal: () => void;
  allLayouts: (DefaultLayoutProps | UserAccountLayout)[];
  setSelectedLayoutId: (val: string) => void;
  selectedLayout: DefaultLayoutProps | UserAccountLayout;
  name: string;
  hideSaveLayout: boolean;
  isChangeControl: boolean;
  changeManagementId: string;
  engineeringStatus?: string;
  changeManagementPermissions?: ChangeManagementPermissions;
  changeManagementSignature?: ChangeManagementSignature;
  onIssue: (data: IssueCommodity[]) => void;
  canAddItem: boolean;
  canDeleteItem: boolean;
  canCloneItem: boolean;
  addHandler: () => void;
  cloneHandler: () => void;
  deleteHandler: () => void;
  hideSearch?: boolean;
}

export const ClientGridHeader: FC<ClientGridHeaderProps> = ({
  onSearchTextChanged,
  layoutVisibleFlag,
  setLayoutVisibleFlag,
  saveLayoutModal,
  allLayouts,
  setSelectedLayoutId,
  selectedLayout,
  name,
  hideSaveLayout,
  isChangeControl,
  changeManagementId,
  engineeringStatus,
  changeManagementPermissions,
  changeManagementSignature,
  onIssue,
  canAddItem,
  canCloneItem,
  canDeleteItem,
  addHandler,
  cloneHandler,
  deleteHandler,
  hideSearch = false,
}) => {
  const theme = useTheme();
  const { getMultiLingualTextByKey } = useLanguage();
  const [quickFilterText, setQuickFilterText] = useState('');
  const { deleteUserAccountLayout } = useDeleteUserLayout();
  const modalResponse = useRef<Question>(Question.NOT_ASKED);
  const { visible, open, close } = useModalState(false);
  // const [userPreferences] = useLocalStorage('User Preferences', true); // workAround, will remove this part when the UI Screen is ready
  const userPreferences = false;

  const SelectedLayoutItemComponent = ({ item }: { item: UserAccountLayout }) => {
    return <Text>{item.description}</Text>;
  };

  const LayoutItemComponent = ({ item }: { item: UserAccountLayout }) => {
    return (
      <Box justifyContent={{ sm: 'space-between' }}>
        <Text>{item.description}</Text>
        <Box
          onClick={() => {
            deleteUserAccountLayout(item.id, item.description);
          }}
        >
          {item.id !== defaultLayout.id && <Icon icon="trash-can" iconStyle="regular" fillColor="red.700" />}
        </Box>
      </Box>
    );
  };

  const handleQuestions = (questionResponses: QuestionModalResponse[]) => {
    const questionResponse = questionResponses.find(
      (response) => response.messageKey === UiMessageKey.ARE_YOU_SURE_YOU_WANT_TO_DELETE_THIS_ITEM
    );

    if (questionResponse) {
      modalResponse.current = questionResponse.response;
    }
    close();
    if (modalResponse.current === Question.REPLY_YES) {
      deleteHandler();
    } else {
      return;
    }
  };

  return (
    <Box justifyContent={{ sm: 'space-between' }} mb={{ sm: '500' }}>
      <Box flexGrow={{ sm: 1 }}>
        {!hideSearch && (
          <Box mr={{ sm: '450' }} flexBasis={{ sm: '40%' }}>
            <SearchField
              placeholder={getMultiLingualTextByKey(UiTextEnum.SEARCH)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setQuickFilterText(e.target.value);
                onSearchTextChanged(e.target.value);
              }}
              size={TextFieldSize.SMALL}
              iconSize={IconSize.SMALL}
              quickFilterText={quickFilterText}
            />
          </Box>
        )}
        {!hideSaveLayout && (
          <>
            <Box flexBasis={{ sm: '30%' }}>
              <Select
                items={allLayouts}
                itemComponent={LayoutItemComponent}
                selectedComponent={SelectedLayoutItemComponent}
                popperStyle={{
                  zIndex: '2',
                  fontSize: theme.fontSizes[300],
                }}
                onSelectedItemChange={(changes) => {
                  setSelectedLayoutId(changes.selectedItem.id);
                  setLayoutVisibleFlag(false);
                  setQuickFilterText('');
                  onSearchTextChanged('');
                }}
                selectedItem={selectedLayout}
                dropDownIconName="angle-up"
                dropDownIconStyle="solid"
                size={TextFieldSize.SMALL}
              />
            </Box>

            {layoutVisibleFlag && !quickFilterText && (
              <Box ml={{ sm: '450' }}>
                <ButtonStyled size={ButtonSize.SMALL} onClick={saveLayoutModal}>
                  <Icon icon="floppy-disk" iconStyle="solid" style={{ marginRight: theme.spacings[400] }} />
                  <MultiLingualText>Save Layout</MultiLingualText>
                </ButtonStyled>
              </Box>
            )}
          </>
        )}
      </Box>
      <Box flexBasis={{ sm: '30%' }} justifyContent={{ sm: 'flex-end' }}>
        {isChangeControl && changeManagementPermissions && (
          <ChangeManagementToolbarProvider
            id={changeManagementId}
            name={name}
            engineeringStatus={engineeringStatus ?? ''}
            signature={changeManagementSignature}
            permissions={changeManagementPermissions}
            enabled={isChangeControl}
            onIssue={onIssue}
          >
            <ChangeManagementToolBar />
          </ChangeManagementToolbarProvider>
        )}
        <Box mr={{ sm: '200' }}>
          <CreateAction canAddItem={Boolean(canAddItem)} addHandler={addHandler} />
        </Box>
        <Box mr={{ sm: '200' }}>
          <CloneAction enableClone={Boolean(canCloneItem)} cloneHandler={cloneHandler} />
        </Box>
        <Box mr={{ sm: '200' }}>
          <DeleteAction
            enableDelete={Boolean(canDeleteItem)}
            deleteHandler={() => (userPreferences ? open() : deleteHandler())}
          />
        </Box>
        <Box pr={{ sm: '200' }}>
          <CommodityMenu />
        </Box>
      </Box>
      {visible && (
        <MessageModal
          onSubmit={handleQuestions}
          messageKeys={userPreferences ? [UiMessageKey.ARE_YOU_SURE_YOU_WANT_TO_DELETE_THIS_ITEM] : []}
        ></MessageModal>
      )}
    </Box>
  );
};
